import type { FC } from 'react';
import { useState, useRef } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  alpha,
  Box,
  IconButton,
  MenuIcon,
  Typography,
  useTheme,
} from '@/shared/ui/mui';
import { SideMenu } from '@/app/layouts/side-menu/components/SideMenu';
import { AudienceToggle } from '@/features/landing/unified/components/AudienceToggle';
import type { Audience } from '@/features/landing/unified/hooks/useAudienceToggle';
import type { NavigationIdentity } from '../hooks';
import { AuthButtons } from './AuthButtons';
import { DesktopLandingNav } from './DesktopLandingNav';
import { DesktopProfileMenu } from './DesktopProfileMenu';
import { FeaturesMegaMenu } from './FeaturesMegaMenu';
import { MobileLandingDrawer } from './MobileLandingDrawer';

export interface NavbarContentProps {
  identity: NavigationIdentity;
  isLargeScreen: boolean;
  isHomePage: boolean;
  isScrolled: boolean;
  audience?: Audience;
  onAudienceToggle?: (audience: Audience) => void;
}

export const NavbarContent: FC<NavbarContentProps> = ({
  identity,
  isLargeScreen,
  isHomePage,
  isScrolled,
  audience,
  onAudienceToggle,
}) => {
  const { isAuthenticated, user, userType, menuItems, commonItems, logout } = identity;
  const theme = useTheme();
  const isMobile = !isLargeScreen;
  const navbarRef = useRef<HTMLDivElement>(null);
  const featuresAnchorRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();
  const isFeaturePage = location.pathname.startsWith('/features');
  const isProviderView = audience !== 'clients';
  const showDesktopNav = (isHomePage || isFeaturePage) && !isMobile;
  const showMobileNav = (isHomePage || isFeaturePage) && isMobile;

  const [isFeaturesMenuOpen, setIsFeaturesMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isMobileAuthMenuOpen, setIsMobileAuthMenuOpen] = useState(false);

  const handleFeaturesToggle = () => {
    setIsProfileMenuOpen(false);
    setIsFeaturesMenuOpen(prev => !prev);
  };

  const handleProfileToggle = () => {
    setIsFeaturesMenuOpen(false);
    setIsProfileMenuOpen(prev => !prev);
  };

  const handleFeaturesClose = (event: Event | React.SyntheticEvent) => {
    if (
      featuresAnchorRef.current &&
      featuresAnchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setIsFeaturesMenuOpen(false);
  };

  const scrollToAnchor = (anchor: string) => {
    const el = document.querySelector(anchor);
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const isDarkNav = isHomePage && !isScrolled;
  const navLinkColor = isScrolled
    ? theme.palette.grey[500]
    : alpha(theme.palette.common.white, 0.8);

  return (
    <Box ref={navbarRef} sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: { xs: 1, sm: 2 } }}>
      {/* Logo */}
      <Typography
        variant="h6"
        component={RouterLink}
        to="/"
        sx={{
          flexGrow: isHomePage || isFeaturePage ? 0 : 1,
          mr: isHomePage || isFeaturePage ? 'auto' : 0,
          color: isHomePage && !isScrolled ? theme.palette.common.white : theme.palette.primary.main,
          fontWeight: 700,
          letterSpacing: '-0.5px',
          textDecoration: 'none',
          textShadow: 'none',
          fontSize: { xs: '1.1rem', sm: '1.3rem' },
        }}
      >
        Donvio
      </Typography>

      {/* Audience toggle (only on home page) */}
      {isHomePage && audience && onAudienceToggle && (
        <Box sx={{ mr: { xs: 'auto', sm: 0 } }}>
          <AudienceToggle
            audience={audience}
            onToggle={onAudienceToggle}
            variant={isScrolled ? 'light' : 'dark'}
          />
        </Box>
      )}

      {/* Desktop nav links */}
      {showDesktopNav && (
        <DesktopLandingNav
          isHomePage={isHomePage}
          isProviderView={isProviderView}
          isScrolled={isScrolled}
          navLinkColor={navLinkColor}
          featuresAnchorRef={featuresAnchorRef}
          isFeaturesMenuOpen={isFeaturesMenuOpen}
          onFeaturesToggle={handleFeaturesToggle}
          onScrollToAnchor={scrollToAnchor}
        />
      )}

      {/* Features mega-menu */}
      {showDesktopNav && isProviderView && (
        <FeaturesMegaMenu
          open={isFeaturesMenuOpen}
          anchorEl={navbarRef.current}
          anchorWidth={navbarRef.current?.offsetWidth}
          isDarkNav={isDarkNav}
          onClose={handleFeaturesClose}
          onItemClick={() => setIsFeaturesMenuOpen(false)}
          featuresAnchorRef={featuresAnchorRef}
        />
      )}

      {/* Desktop: auth buttons or profile menu */}
      {!isMobile && (
        !isAuthenticated ? (
          <AuthButtons loginContext={audience === 'providers' ? 'provider' : undefined} />
        ) : (
          <DesktopProfileMenu
            user={user}
            menuItems={menuItems}
            commonItems={commonItems}
            isHomePage={isHomePage}
            isScrolled={isScrolled}
            isOpen={isProfileMenuOpen}
            onToggle={handleProfileToggle}
            onClose={() => setIsProfileMenuOpen(false)}
            logout={logout}
          />
        )
      )}

      {/* Mobile hamburger: unauthenticated on landing/feature pages */}
      {isMobile && showMobileNav && !isAuthenticated && (
        <IconButton
          size="large"
          edge="end"
          sx={{
            color: isHomePage && !isScrolled ? theme.palette.common.white : theme.palette.common.black,
          }}
          onClick={() => setIsMobileNavOpen(true)}
          aria-label="Open navigation menu"
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Mobile landing drawer - unauthenticated only */}
      {showMobileNav && !isAuthenticated && (
        <MobileLandingDrawer
          open={isMobileNavOpen}
          onClose={() => setIsMobileNavOpen(false)}
          isHomePage={isHomePage}
          isProviderView={isProviderView}
        />
      )}

      {/* Mobile hamburger: authenticated users */}
      {isMobile && isAuthenticated && (
        <>
          <IconButton
            size="large"
            edge="end"
            sx={{
              color: isHomePage && !isScrolled ? theme.palette.common.white : theme.palette.common.black,
            }}
            onClick={() => setIsMobileAuthMenuOpen(true)}
            aria-label="Open navigation menu"
          >
            <MenuIcon />
          </IconButton>
          <SideMenu
            open={isMobileAuthMenuOpen}
            onClose={() => setIsMobileAuthMenuOpen(false)}
            userType={userType}
            menuItems={menuItems}
            commonItems={commonItems}
            user={user}
          />
        </>
      )}
    </Box>
  );
};
